import React, { useState, useEffect } from "react";
import {
  UserPlus,
  Shield,
  Smartphone,
  Globe,
  Lock,
  XCircle,
  Edit3,
  Trash2,
  Crown,
  Phone,
  Mail,
  Hash,
} from "lucide-react";
import api from "../api/axiosInstance";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentUser] = useState(
    JSON.parse(localStorage.getItem("user_session")),
  );

  const initialForm = {
    id: "",
    username: "",
    email: "",
    phone: "",
    password: "",
    role: "Supervisor",
    status: "Active",
    type: "Staff",
    branch_id: 1,
  };
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get("get_users.php");
      setUsers(res.data.users || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEdit(false);
    setFormData(initialForm);
  };

  const handleEdit = (user) => {
    setIsEdit(true);
    setFormData({
      ...user,
      password: "", // Password stays empty unless changing
      branch_id: user.branch_id || 1,
      type: user.type || "staff",
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (formData.phone && !/^\d{10}$/.test(formData.phone)) {
      alert("Please enter a valid 10-digit mobile number.");
      return;
    }
    try {
      await api.post("manage_users.php", {
        ...formData,
        action: isEdit ? "update" : "create",
      });
      closeModal();
      loadData();
    } catch (err) {
      alert("Action failed. Check permissions or if username exists.");
    }
  };

  const canManageRole = (targetRole) => {
    if (currentUser.role === "Owner" || currentUser.role === "owner") return true;
    if (currentUser.role === "Admin" || currentUser.role === "admin")
      return !["Owner", "Admin", "owner", "admin"].includes(targetRole);
    return false;
  };

const getAvailableRoles = () => {
  if (currentUser.role?.toLowerCase() === "owner") {
    return ["Owner", "Admin", "Supervisor", "Staff", "Office"];
  }

  if (currentUser.role?.toLowerCase() === "admin") {
    return ["Supervisor", "Staff", "Office"];
  }

  return [];
};

  return (
    <div className="space-y-8 p-4 animate-in fade-in duration-500">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic uppercase leading-none">
            Security Center
          </h1>
          <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-2 italic">
            Role: {currentUser.role} Control Panel
          </p>
        </div>
        <button
          onClick={() => {
            setIsEdit(false);
            setFormData(initialForm);
            setShowModal(true);
          }}
          className="bg-indigo-600 text-white px-8 py-4 rounded-3xl font-black text-[10px] uppercase shadow-xl flex items-center gap-2 hover:bg-slate-900 transition-all"
        >
          <UserPlus size={18} /> New Access ID
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {users
          .filter((u) => canManageRole(u.role))
          .map((user) => (
            <div
              key={user.id}
              className="bg-white p-8 rounded-[40px] border border-slate-100 shadow-sm relative hover:shadow-xl transition-all"
            >
              <div className="flex justify-between items-start mb-6">
                <div className="flex gap-2 text-indigo-600">
                  {user.role === "Owner" ? (
                    <Crown size={20} />
                  ) : (
                    <Shield size={20} />
                  )}
                  <span className="text-sm font-black uppercase italic">
                    {user.role}
                  </span>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(user)}
                    className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                  >
                    <Edit3 size={16} />
                  </button>
                  <button
                    onClick={async () => {
                      if (window.confirm("Delete?")) {
                        await api.post("manage_users.php", {
                          id: user.id,
                          action: "delete",
                        });
                        loadData();
                      }
                    }}
                    className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <h3 className="text-xl font-black text-slate-900 uppercase italic truncate leading-none mb-1">
                {user.username}
              </h3>
              <div className="space-y-1 mb-6">
                <p className="text-[10px] font-bold text-slate-400 italic flex items-center gap-1">
                  <Mail size={10} /> {user.email || "No Email"}
                </p>
                <p className="text-[10px] font-bold text-slate-400 italic flex items-center gap-1">
                  <Phone size={10} /> {user.phone || "No Phone"}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 pt-6 border-t border-slate-50">
                <div
                  className={`px-3 py-2 rounded-xl text-[8px] font-black uppercase text-center border ${user.status === "Active" ? "bg-emerald-50 border-emerald-100 text-emerald-600" : "bg-slate-50 border-slate-100 text-slate-400"}`}
                >
                  {user.status}
                </div>
                <div className="px-3 py-2 rounded-xl bg-slate-50 border border-slate-100 text-slate-600 text-[8px] font-black uppercase text-center">
                  Branch: {user.branch_id}
                </div>
              </div>
            </div>
          ))}
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <form
            onSubmit={handleSave}
            className="bg-white rounded-[48px] p-10 w-full max-w-xl shadow-2xl animate-in zoom-in-95 duration-200 overflow-y-auto max-h-[95vh]"
          >
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-2xl font-black italic uppercase tracking-tighter">
                {isEdit ? "Update" : "Create"} Identity
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="p-2 text-slate-400 hover:bg-slate-100 rounded-full"
              >
                <XCircle size={24} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-[10px] font-black uppercase text-slate-400 ml-4 mb-1 block">
                  Username
                </label>
                <input
                  required
                  className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold text-sm outline-none"
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase text-slate-400 ml-4 mb-1 block">
                  Email
                </label>
                <input
                  type="email"
                  className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold text-sm outline-none"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase text-slate-400 ml-4 mb-1 block">
                  Phone (10 Digits)
                </label>
                <input
                  type="tel"
                  maxLength={10}
                  placeholder="e.g. 9876543210"
                  className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold text-sm outline-none"
                  value={formData.phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setFormData({ ...formData, phone: val });
                  }}
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] font-black uppercase text-slate-400 ml-4 mb-1 block">
                  {isEdit ? "New Password (Optional)" : "Password"}
                </label>
                <input
                  required={!isEdit}
                  type="password"
                  className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold text-sm outline-none"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase text-slate-400 ml-4 mb-1 block">
                  Branch ID
                </label>
                <input
                  type="number"
                  className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold text-sm outline-none"
                  value={formData.branch_id}
                  onChange={(e) =>
                    setFormData({ ...formData, branch_id: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase text-slate-400 ml-4 mb-1 block">
                  Type
                </label>
                <select
                  disabled={false}
                  className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold text-sm outline-none"
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                >
                  <option value="Staff">Staff</option>
                  <option value="Client">Client</option>
                  <option value="Contractor">Contractor</option>
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label className="text-[10px] font-black uppercase text-slate-400 ml-4 mb-2 block">
                System Role
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {getAvailableRoles().map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setFormData({ ...formData, role: r })}
                    className={`p-3 rounded-xl font-black text-[9px] uppercase transition-all ${formData.role === r ? "bg-indigo-600 text-white shadow-lg" : "bg-slate-100 text-slate-400"}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-4 mt-10">
              <button
                type="button"
                onClick={closeModal}
                className="flex-1 bg-slate-100 text-slate-400 p-5 rounded-3xl font-black uppercase italic tracking-widest hover:bg-slate-200 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-[2] bg-slate-900 text-white p-5 rounded-3xl font-black uppercase italic tracking-widest shadow-xl hover:bg-indigo-600 transition-all"
              >
                {isEdit ? "Update ID" : "Create ID"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
